import { KPICalculation, KPICalculationPart } from './../../../services/project-kpi.service';
import { Subscription, ReplaySubject } from 'rxjs';
import { ProjectService } from './../../../services/project.service';
import { BbTableSelectOption, TableRow } from './../../../services/bb-table.service';
import { Project, ProjectStatsReference, ProjectStep, ProjectChapter, ProjectStatsConfiguration, ProjectPerformanceStats } from './../../../interfaces/project';
import { Component, Input, forwardRef, SimpleChanges, HostListener, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { BbTableService } from '../../../services/bb-table.service';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Publication } from '../../../interfaces/publication';
import { PublicationService } from '../../../services/publication.service';
import { debounceTime } from 'rxjs/operators';
import { ModalDefinition, OverlayService } from '../../../services/overlay.service';

@Component({
	selector: 'project-kpi-maker',
	templateUrl: './project-kpi-maker.component.html',
	styleUrls: ['./project-kpi-maker.component.scss'],
	providers: [
		BbTableService,
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => ProjectKpiMakerComponent),
			multi: true
		}
	]
})

export class ProjectKpiMakerComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {

	@Input() project: Project;
	@Input() goal: number;
	@Input() mode: string;
	@Input() kpiId: string;
	@Input() isMonthly: boolean;
	@Input() isPercentage: boolean;
	@Input() kpi: KPICalculation;
	@Input() datafeedVars: any[];
	@Input() configuration: ProjectStatsConfiguration;

	propagateChange: any;
	propagateTouched: any;
	editMode = false;
	justAddedIndex: number;
	charWidth: number;
	mathFieldText = '';
	mathFieldArr: any[];
	calculationParts: any[];
	operators: string[];
	operatorCounts: any;
	mediaclips: any[];
	tableRows: TableRow[];
	projectWidgets: any[];
	projectWidgetsRef: any = {};
	showOnlyKPIs = true;
	sum: any;
	haveStats = false;
	filters = {
		title: '',
		widget: ''
	};
	tableFilter = new UntypedFormControl();
	cursorIndex = 0;
	timeZoneName = 'Europe/Berlin';
	params: any;
	usedMetricsRef: any = {};
	hoveredMetric = '';

	statsRefSub: Subscription;
	statsRef: ProjectStatsReference;
	projectWithStatsSub: Subscription;
	projectWithStats: Project;
	projectPerformanceStatsSub: Subscription;
	projectPerformanceStats: ProjectPerformanceStats;
	projectPerfomanceStatsSubject: ReplaySubject<boolean>;

	private subscriptions = new Subscription();

	constructor(
		private bbTableService: BbTableService,
		private projectService: ProjectService,
		private translate: TranslateService,
		private overlayService: OverlayService,
		private publicationService: PublicationService
	) {
		this.projectPerfomanceStatsSubject = new ReplaySubject<boolean>(1);
		if (!this.propagateTouched) {
			this.propagateTouched = () => { };
		}
		this.subscriptions.add(this.publicationService.getData().subscribe((pub: Publication) => {
			if (pub && pub.timeZone) {
				this.timeZoneName = pub.timeZone;
			}
		}));

	}

	@HostListener('document:keydown', ['$event'])
	onKeyDown(ev: KeyboardEvent) {
		if (this.editMode) {
			// console.warn('onKeyDown', ev);
			switch (ev.key) {
				default:
					const keys = '()+-/*';
					if (keys.indexOf(ev.key) !== -1) {
						this.addPartFromHotkey(ev.key);
					}
					break;

				case 'ArrowLeft':
					this.moveCursor(this.cursorIndex - 1);
					break;

				case 'ArrowRight':
					this.moveCursor(this.cursorIndex + 1);
					break;

				case 'Backspace':
					if (this.cursorIndex > 0) {
						this.removePart(this.cursorIndex - 1);
						this.moveCursor(this.cursorIndex - 1);
					}
					break;

				case 'Delete':
					// doesn't work, gets caught by project delete..
					// this.removePart(this.cursorIndex);
					return false;
					break;
			}
		}
	}

	ngOnInit() {
		this.bbTableService
			.setConfigurable(false)
			.setSelect(BbTableSelectOption.none)
			.setIdentifier('mediaclips-with-widgets-list');
		this.bbTableService.setLocalFilterFunction(this.customFilterFunction, this);

		this.calculationParts = [{
			label: '(',
			value: '(',
			type: 'grouping'
		},
		{
			label: ')',
			value: ')',
			type: 'grouping'
		}];
		this.mathFieldArr = [];
		this.operatorCounts = {};
		this.operators = ['+', '-', '*', '/'];
		this.operators.forEach((op) => {
			this.operatorCounts[op] = 0;
			this.calculationParts.push({
				label: op,
				value: op,
				type: 'operator'
			});
		});
		this.calculationParts.push({
			projectLevel: true,
			type: 'variable',
			entity: 'project',
			property: 'inits',
			label: this.translate.instant('PROJECT_PLAYER_LOADS'),
			icon: 'paper-plane'
		});
		this.calculationParts.push({
			projectLevel: true,
			type: 'variable',
			entity: 'project',
			property: 'views',
			label: 'Project starts',
			icon: 'paper-plane'
		});
		this.calculationParts.push({
			projectLevel: true,
			type: 'variable',
			entity: 'project',
			property: 'visitors',
			label: this.translate.instant('UNIQUE_VIEWERS'),
			icon: 'paper-plane'
		});
		this.calculationParts.push({
			type: 'variable',
			entity: 'project',
			property: 'uniqueClips',
			label: 'Unique clip views',
			icon: 'paper-plane'
		});
		this.calculationParts.push({
			type: 'variable',
			entity: 'project',
			property: 'preciseViewtime',
			label: 'Precise view-time',
			icon: 'paper-plane'
		});
		this.calculationParts.push({
			type: 'variable',
			entity: 'project',
			property: 'totalClips',
			label: 'Total clip views',
			icon: 'paper-plane'
		});

		this.calculationParts.push({
			type: 'variable',
			entity: 'number',
			value: 100,
			label: 'Number'
		});

		this.fillWidgets(this.project);

		if (this.kpi.parts.filter((kpiPart: any) => kpiPart.property === 'visitors' || kpiPart.property === 'views').length > 0) {
			this.projectPerfomanceStatsSubject.subscribe(() => {
				this.getProjectPerformanceStats();
			});
		}

		this.statsRefSub = this.projectService.statsRefSubject.subscribe((statsRef: ProjectStatsReference) => {
			this.statsRef = statsRef;
			this.projectPerfomanceStatsSubject.next(true);
			this.onSumChange(true);
		});
		this.projectWithStatsSub = this.projectService.projectWithStatsSubject.subscribe((projectWithStats: Project) => {
			this.projectWithStats = projectWithStats;
			this.fillWidgets(projectWithStats);
		});
		this.subscriptions.add(this.tableFilter.valueChanges.pipe(
			debounceTime(300)
		).subscribe((value) => {
			this.filterTable(value);
		}));

		if (history.state.kpiId === this.kpi.id) {
			this.editMode = true;
		}

		// this.bindHotKeys();
	}

	moveCursor(pos: number) {
		if (pos > this.mathFieldArr.length) {
			this.cursorIndex = this.mathFieldArr.length;
		} else if (pos < 0) {
			this.cursorIndex = 0;
		} else {
			this.cursorIndex = pos;
		}
	}

	addPartFromHotkey(k: string): boolean {
		let part: KPICalculationPart = null;
		switch (k) {
			case '(':
			case ')':
				part = {
					label: k,
					value: k,
					type: 'grouping'
				};
				break;

			case '+':
			case '-':
			case '*':
			case '/':
				part = {
					label: k,
					value: k,
					type: 'operator'
				};
				break;
		}

		if (part) {
			this.addPart(part);
		}

		return !part;
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes && changes.datafeedVars && changes.datafeedVars.currentValue) {
			if (this.calculationParts && this.datafeedVars && this.datafeedVars.length > 0) {
				if (changes.datafeedVars.previousValue) {
					// first, remove old datafeedvars (if any) from calculationparts
					for (let i = changes.datafeedVars.previousValue.length - 1; i >= 0; --i) {
						const property = changes.datafeedVars.previousValue[i].property;
						for (let j = this.calculationParts.length - 1; j >= 0; --j) {
							if (this.calculationParts[j].property === property) {
								this.calculationParts.splice(j, 1);
							}
						}
					}
				}
				// then add new datafeedVars:
				for (let i = 0; i < this.datafeedVars.length; i++) {
					this.calculationParts.push(this.datafeedVars[i]);
				}
			}
		}

		if (typeof changes.goal !== 'undefined' && !changes.goal.firstChange) {
			this.onSumChange(true);
		}
	}

	ngOnDestroy() {
		if (this.projectWithStatsSub) {
			this.projectWithStatsSub.unsubscribe();
		}
		if (this.statsRefSub) {
			this.statsRefSub.unsubscribe();
		}
		if (this.projectPerformanceStatsSub) {
			this.projectPerformanceStatsSub.unsubscribe();
		}
	}

	customFilterFunction(row: any, filter: string) {
		const rowData = row.data;

		// leaving below in here for if we do want to later filter on mediaclip title
		// let ret = (rowData && (
		// 		rowData.title && rowData.title.toLowerCase().indexOf(filter.toLowerCase()) !== -1)
		// 	);

		let ret = false;
		if (!ret && rowData.widgets && rowData.widgets.length) {
			rowData.widgets.forEach((widget: any) => {
				if (widget && (
					(widget.useAsKPI === 'true' || !this.showOnlyKPIs) &&
					(widget.label && widget.label.toLowerCase().indexOf(filter.toLowerCase()) !== -1)
				)) {
					ret = true;
				}
				if (widget && widget.widgets && widget.widgets.length) {
					widget.widgets.forEach((appWidget: any) => {
						if (appWidget && (
							(widget.useAsKPI === 'true' || !this.showOnlyKPIs) &&
							(appWidget.label && appWidget.label.toLowerCase().indexOf(filter.toLowerCase()) !== -1)
						)) {
							ret = true;
						}
					});
				}
			});
		}
		return ret;
	}

	checkSearchValue(widget: any) {
		let ret = false;
		if (widget && typeof widget.label === 'string' && this.tableFilter && this.tableFilter.value) {
			ret = widget.label.toLowerCase().indexOf(this.tableFilter.value.toLowerCase()) !== -1;
			if (!ret && widget.widgets && widget.widgets.length) {
				widget.widgets.forEach((appWidget: any) => {
					ret = appWidget && appWidget.label && appWidget.label.toLowerCase().indexOf(this.tableFilter.value.toLowerCase()) !== -1;
				});
			}
		}
		return ret;
	}

	filterTable(value: string) {
		this.bbTableService.setLocalFilter(value);
	}

	onShowKPIChange() {
		this.bbTableService.setLocalFilter(this.tableFilter.value);
	}

	writeValue(val: any) {
		// incoming from external change
		if (val) {
			this.mathFieldArr = val;
			this.onSumChange(true);
		}
	}

	registerOnChange(fn: any) {
		this.propagateChange = fn;
	}

	registerOnTouched(fn: any) {
		this.propagateTouched = fn;
	}

	done() {
		this.propagateTouched();
	}

	setDisabledState() {
		// optional
	}

	log(t: any) {
		console.warn('LOG', t);
	}

	numberValChanged(ev: any, part: KPICalculationPart) {
		if (ev && ev.target && ev.target.textContent) {
			part.value = Number(ev.target.textContent);
			this.onSumChange();
		}
	}

	getStats(part: KPICalculationPart) {
		if (this.statsRef && this.statsRef[part.entity] && this.statsRef[part.entity][part.property]) {
			if (part.entity === 'project') {
				return this.statsRef[part.entity][part.property];
			} else {
				return this.statsRef[part.entity][part.property][part.id + (part.parentEntityId ? '_' + part.parentEntityId : '')];
			}
		}
		return 0;
	}

	getKpiParams(configuration: ProjectStatsConfiguration) {
		const params: any = {
			TZ: this.timeZoneName,
			granularity: 'month',
			fromDate: configuration.statsDateStart.toISOString(),
			toDate: configuration.statsDateEnd.toISOString(),
		};
		return params;
	}

	getProjectPerformanceStats() {
		let uniqueVisitors: number;
		const params = this.getKpiParams(this.configuration);

		this.projectService.setProjectPerformanceStatsData(params);

		if (this.projectPerformanceStatsSub) {
			return;
		}

		this.projectPerformanceStatsSub = this.projectService.performanceStatsDataSubject.subscribe((projectPerformanceStats: any) => {
			this.projectPerformanceStats = projectPerformanceStats;

			// retreive project stats from performance instead of stats (cannnot join on id or on label therefor on cumulative.label)
			if (this.statsRef?.project && this.projectPerformanceStats) {
				const projectViews = this.projectPerformanceStats.items.find(kpi => kpi.cumulative.label === 'views on Project starts');
				this.statsRef.project.views = projectViews?.cumulative?.result || 0;
			}

			for (let i = 0; i < projectPerformanceStats.items.length; i++) {
				if (projectPerformanceStats.items[i].id && projectPerformanceStats.items[i].total) {
					uniqueVisitors = projectPerformanceStats.items[i].total.result;
					if (this.statsRef && this.statsRef.project) {
						this.statsRef.project.visitors = uniqueVisitors;
						this.onSumChange(true);
					}
				}
			}
		});

	}

	fillWidgets(project: Project) {

		this.projectWidgets = project?.projectWidgets || [];
		this.projectWidgetsRef = {};
		this.projectWidgets.forEach(projectWidget => {
			this.projectWidgetsRef[projectWidget.id] = projectWidget;
			if (projectWidget?.widgets?.length) {
				projectWidget.widgets.forEach(projectSubWidget => {
					this.projectWidgetsRef[projectSubWidget.id] = projectSubWidget;
				});
			}
		});

		this.mediaclips = [];

		// code to fix previously saved project kpiCalculations
		// let tmp = {};

		if (project && project.chapters) {
			project.chapters.forEach((chapter: ProjectChapter) => {
				chapter.steps.forEach((step: ProjectStep) => {
					if (chapter && step.entityType && step.entityType.toLowerCase() === 'mediaclip' && step.entity) {
						const mc: any = {
							id: step.entityId,
							title: step.entity.title,
							widgets: step.widgets
						};
						if (step.stats) {
							mc.stats = step.stats;
						}
						this.mediaclips.push(mc);

						// code to fix previously saved project kpiCalculations
						// step.widgets.forEach((w: any) => {
						// 	tmp['w' + w.id] = w;
						// 	tmp['w' + w.id].parentEntityId = mc.id;
						// })
					}
				});
			});
		}

		// code to fix previously saved project kpiCalculations
		// if (project.kpiCalculations) {
		// 	project.kpiCalculations.forEach((kpiC: KPICalculation) => {
		// 		// console.warn('kpiC', kpiC);
		// 		if (kpiC && !kpiC.id) {
		// 			kpiC.id = this.generateId();
		// 		}
		// 		kpiC.parts.forEach((kpiP: KPICalculationPart) => {
		// 			let t = tmp['w' + kpiP.id];
		// 			if (t && t.parentEntityId) {
		// 				kpiP.parentEntityId = t.parentEntityId;
		// 				kpiP.parentEntityType = 'MediaClip';
		// 			}
		// 			// console.warn('kpiP', kpiP);
		// 		})
		// 	});
		// }


		this.bbTableService
			.setData(this.mediaclips);

		this.bbTableService.rowsRetrievedObservable.subscribe((rows: TableRow[]) => {
			this.tableRows = rows;
		});

		this.bbTableService.getRows();
	}

	mathFieldChanged() {
		// this.mathFieldText
	}

	/*
	Project: inits / views
	Clip: Views, kpi show / kpi click
	*/

	clear() {
		this.mathFieldArr = [];
		this.onSumChange();
	}

	removeLast() {
		this.mathFieldArr.splice(-1, 1);
		this.onSumChange();
	}

	removePart(i: number) {
		this.mathFieldArr.splice(i, 1);
		this.onSumChange();
	}

	groupAll() {
		this.mathFieldArr.push({
			type: 'grouping',
			value: ')',
			label: ')'
		});
		this.mathFieldArr.unshift({
			type: 'grouping',
			value: '(',
			label: '('
		});
		this.onSumChange();
	}

	isDisabled(part: KPICalculationPart): boolean {
		let ret = false;
		return ret;
		const lastPart = this.mathFieldArr[this.mathFieldArr.length - 1];
		switch (part.type) {
			case 'grouping':
				if (part.value === '(') {
					ret = (
						lastPart && (
							lastPart.type !== 'operator' ||
							(lastPart.type === 'grouping' && lastPart.value !== '(')
						)
					);
				} else if (part.value === ')') {
					ret = (!this.operatorCounts['('] || this.operatorCounts['('] <= this.operatorCounts[')']) ||
						!lastPart || (
						lastPart.type === 'operator'
					);
				}
				break;
			case 'variable':
				ret = lastPart && (
					lastPart.type === 'variable' ||
					(lastPart.type === 'grouping' && lastPart.value === ')')
				);
				break;
			case 'operator':
				ret = !lastPart || lastPart.type === 'operator';
				break;
		}

		return ret;
	}

	onSumChange(noPropagation: boolean = false) {
		this.updateOperatorCounts();
		this.makeSum(this.mathFieldArr);
		if (!noPropagation) {
			this.propagateChange(this.mathFieldArr);
		}
		// ensure the cursor is within range:
		this.moveCursor(this.cursorIndex);
	}

	checkNaN(n: any) {
		return isNaN(n);
	}

	makeSum(fieldArr: KPICalculationPart[]) {
		this.sum = {
			evalString: ''
		};
		let testStats = true;
		this.usedMetricsRef = {};
		fieldArr.forEach((part: KPICalculationPart) => {
			const refStr = (part.parentEntityType ? part.parentEntityType + part.parentEntityId : '') + part.entity + part.id + part.property;
			this.usedMetricsRef[refStr] = true;
			this.sum.evalString += ' ';
			const num = part.entity && part.entity !== 'number' ? this.getStats(part) || 0 : part.value;
			this.sum.evalString += num;
			if (part.entity !== 'number' && num > 0) {
				testStats = true;
			}
		});

		this.haveStats = testStats;

		try {
			this.sum.result = eval(this.sum.evalString);
		} catch (e) {
			this.sum.result = false;
		}
	}

	updateOperatorCounts() {
		this.operatorCounts = {};
		this.mathFieldArr.forEach((part) => {
			this.operatorCounts[part.value] = this.operatorCounts[part.value] ? this.operatorCounts[part.value] + 1 : 1;
		});
	}

	addPart(part: KPICalculationPart) {
		const newPart = { ...part };
		this.mathFieldArr.splice(this.cursorIndex, 0, newPart);
		// this.mathFieldArr.push(newPart);
		this.moveCursor(this.cursorIndex + 1);
		this.onSumChange();
	}

	calculateSum() {
		if (this.sum && this.sum.result && !isNaN(this.sum.result)) {
			let finalSum = this.sum.result;
			if (this.isPercentage) {
				finalSum *= 100;
				return finalSum.toPrecision(3) + '%';
			} else if (this.isMonthly) {
				const numberOfMonths: number = this.projectService.getMonthsInStatsDateRange();
				return (finalSum / (numberOfMonths !== 0 ? numberOfMonths : 1)) + ' / mo';
			}
			return finalSum;

		} else {
			return this.translate.instant('INVALID_CALCULATION');
		}
	}

	calculatePercentage() {
		if (this.sum && this.sum.result && !isNaN(this.sum.result)) {
			let finalPercentage = this.sum.result / this.goal;
			finalPercentage *= 100;
			return finalPercentage.toPrecision(3) + '%';
		} else {
			return this.translate.instant('INVALID_CALCULATION');
		}
	}

	onPreviewLinkClick(event: MouseEvent, clipId: string, title: string) {
		this.showClipDetails(clipId, title);
	}

	showClipDetails(id: string, title: string) {
		this.configuration = { ...this.configuration, ...this.projectService.getStatsConfiguration() };
		if (!this.project) {
			this.project = this.projectService.getProject();
		}
		const statsStep = this.projectService.findStep(id, this.project);
		// console.warn('showClipDetails project', this.project)
		const modal: ModalDefinition = {
			icon: 'film',
			title,
			component: {
				name: 'stats-progress',
				inputs: {
					clipId: statsStep.entityId,
					parentEntityId: this.project.id,
					parentEntityType: 'Project',
					configuration: this.configuration,
					step: statsStep
				}
			}
		};

		this.overlayService.createModal(modal);
	}

	generateId() {
		return 'k' + Math.random().toString(36).substr(2, 16);
	}
}
